import React from 'react';
import { useUserDataContext } from '../../contexts/UserDataContext';
import './MarginPanel.css';

/**
 * MarginPanel component for displaying user margin information
 * Shows balance, free margin, and used margin
 */
const MarginPanel = () => {
  const { userData, isSubscribed, isLoading, error } = useUserDataContext();
  
  // Format number with commas and 2 decimal places
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '--';
    return parseFloat(num).toLocaleString(undefined, { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Calculate used margin (balance - freeMargin)
  const calculateUsedMargin = () => {
    if (!userData) return null;
    
    const { balance, freeMargin } = userData;
    if (balance === undefined || freeMargin === undefined) return null;
    
    return Math.max(0, balance - freeMargin);
  };

  // Calculate margin level as percentage (balance / used margin * 100)
  const calculateMarginLevel = () => {
    if (!userData) return null;
    
    const usedMargin = calculateUsedMargin();
    if (!usedMargin || usedMargin === 0 || !userData.balance) return null;
    
    return (userData.balance / usedMargin) * 100;
  };

  // Format margin level with percentage
  const formatMarginLevel = () => {
    const level = calculateMarginLevel();
    if (level === null) return '--';
    
    return `${level.toFixed(2)}%`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="mp-container">
        <div className="mp-stats">
          <div className="mp-stat-item mp-loading">
            <div className="mp-stat-label">Account Balance</div>
            <div className="mp-stat-value">--</div>
          </div>
          <div className="mp-stat-item mp-loading">
            <div className="mp-stat-label">Available Margin</div>
            <div className="mp-stat-value">--</div>
          </div>
          <div className="mp-stat-item mp-loading">
            <div className="mp-stat-label">Used Margin</div>
            <div className="mp-stat-value">--</div>
          </div>
          <div className="mp-stat-item mp-loading">
            <div className="mp-stat-label">Margin Level</div>
            <div className="mp-stat-value">--</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mp-container">
        <div className="mp-error">
          <p>Error loading margin data</p>
          <small>{error}</small>
        </div>
      </div>
    );
  }

  // Not subscribed state
  if (!isSubscribed) {
    return (
      <div className="mp-container">
        <div className="mp-not-subscribed">
          <p>Not subscribed to user data</p>
        </div>
      </div>
    );
  }

  // No data state
  if (!userData) {
    return (
      <div className="mp-container">
        <div className="mp-stats">
          <div className="mp-stat-item">
            <div className="mp-stat-label">Account Balance</div>
            <div className="mp-stat-value">--</div>
          </div>
          <div className="mp-stat-item">
            <div className="mp-stat-label">Available Margin</div>
            <div className="mp-stat-value">--</div>
          </div>
          <div className="mp-stat-item">
            <div className="mp-stat-label">Used Margin</div>
            <div className="mp-stat-value">--</div>
          </div>
          <div className="mp-stat-item">
            <div className="mp-stat-label">Margin Level</div>
            <div className="mp-stat-value">--</div>
          </div>
        </div>
      </div>
    );
  }

  // Render with actual data
  return (
    <div className="mp-container">
      <div className="mp-stats">
        <div className="mp-stat-item">
          <div className="mp-stat-label">Account Balance</div>
          <div className="mp-stat-value">{formatNumber(userData.balance)}</div>
        </div>
        <div className="mp-stat-item">
          <div className="mp-stat-label">Available Margin</div>
          <div className="mp-stat-value">{formatNumber(userData.freeMargin)}</div>
        </div>
        <div className="mp-stat-item">
          <div className="mp-stat-label">Used Margin</div>
          <div className="mp-stat-value">{formatNumber(calculateUsedMargin())}</div>
        </div>
        <div className="mp-stat-item">
          <div className="mp-stat-label">Margin Level</div>
          <div className="mp-stat-value">{formatMarginLevel()}</div>
        </div>
      </div>
      
      {userData.trades && userData.trades.length > 0 && (
        <div className="mp-trades-summary">
          <h4>Open Positions: {userData.trades.length}</h4>
        </div>
      )}
      
      {userData.orders && userData.orders.length > 0 && (
        <div className="mp-orders-summary">
          <h4>Active Orders: {userData.orders.length}</h4>
        </div>
      )}
    </div>
  );
};

export default MarginPanel; 